import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Logo } from 'components';
import { Flex, Text, Button, Link, TextField, RadioGroup } from '@radix-ui/themes';
import * as Form from '@radix-ui/react-form';
import { apiURL } from 'helpers/config';

const backendBase = (
  process.env.REACT_APP_BACKEND_BASE_URL
  || (apiURL || '').replace(/api\/v1\/?$/, '')
).replace(/\/?$/, '/');

const oauthFields = (params) => ({
  response_type: params.get('response_type') || '',
  client_id: params.get('client_id') || '',
  redirect_uri: params.get('redirect_uri') || '',
  state: params.get('state') || '',
  code_challenge: params.get('code_challenge') || '',
  code_challenge_method: params.get('code_challenge_method') || '',
  scope: params.get('scope') || '',
  resource: params.get('resource') || '',
});

function ConnectShell({ title, children }) {
  return (
    <Flex style={{ height: '100vh', width: '100vw' }}>
      <Flex direction="column" align="stretch" px="7" py="8" style={{ alignItems: 'center', flex: '1 1 50%' }}>
        <Flex justify="center" align="stretch" width="100%" style={{ alignItems: 'center', flex: '1 0 0' }}>
          <Flex direction="column" gap="7" maxWidth="296px" style={{ alignItems: 'center', justifyContent: 'center', width: '100%' }}>
            <Text size="4" weight="bold" align="center">{title}</Text>
            {children}
          </Flex>
        </Flex>
        <Flex gap="7" justify="center" width="100%">
          <Link size="1" weight="regular" color="gray" href="https://b-connect.co.uk/terms" target="_blank">
            Terms of Service
          </Link>
          <Link size="1" weight="regular" color="gray" href="https://b-connect.co.uk/privacy" target="_blank">
            Privacy Notice
          </Link>
          <Link size="1" weight="regular" color="gray" href="mailto:support@b-connect.co.uk">Support</Link>
        </Flex>
      </Flex>
      <Flex direction="column" align="stretch" style={{
        padding: '80px 120px 0 120px',
        flex: '1 1 50%',
        background: 'url(/LoginPageBG.png) no-repeat center center / cover',
        backgroundBlendMode: 'soft-light',
        alignItems: 'center',
      }}>
        <Logo icon="LogoBWifi" width="40px" height="32px" color="white" />
      </Flex>
    </Flex>
  );
}

export default function McpConnect() {
  const [params] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [consent, setConsent] = useState(null);
  const [organisationId, setOrganisationId] = useState('');

  const signIn = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const response = await axios.post(`${backendBase}oauth/login`, {
        ...oauthFields(params),
        email,
        password,
      });
      const organisations = response.data.organisations || [];
      setConsent(response.data);
      setOrganisationId(String(organisations[0]?.id || ''));
    } catch (requestError) {
      setError(
        requestError.response?.data?.error_description
        || 'Email or password is wrong.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const allowAccess = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const response = await axios.post(`${backendBase}oauth/consent`, {
        consent_token: consent.consent_token,
        organisation_id: Number(organisationId),
      });
      window.location.assign(response.data.redirect_url);
    } catch (requestError) {
      setError(requestError.response?.data?.error_description || 'Could not finish connecting.');
      setSubmitting(false);
    }
  };

  if (consent) {
    return (
      <ConnectShell title="Allow access">
        <Form.Root onSubmit={allowAccess} style={{ width: '100%' }}>
          <Flex direction="column" gap="5">
            <Text size="2" color="gray" align="center">
              {consent.client_name} will read one organisation. It cannot change anything.
            </Text>
            <RadioGroup.Root value={organisationId} onValueChange={setOrganisationId}>
              <Flex direction="column" gap="3" width="100%">
                {consent.organisations.map((organisation) => (
                  <Text as="label" size="2" key={organisation.id} style={{ cursor: 'pointer' }}>
                    <Flex align="center" gap="3">
                      <RadioGroup.Item value={String(organisation.id)} />
                      <Text size="2">{organisation.name}</Text>
                    </Flex>
                  </Text>
                ))}
              </Flex>
            </RadioGroup.Root>
          {error ? (
            <Text size="1" color="red">{error}</Text>
          ) : (
            <Text size="1" style={{ visibility: 'hidden' }}>.</Text>
          )}
          <Button type="submit" size="2" variant="solid" disabled={submitting} style={{ alignSelf: 'stretch' }}>
            Allow access
          </Button>
          </Flex>
        </Form.Root>
      </ConnectShell>
    );
  }

  return (
    <ConnectShell title="Sign in">
      <Text size="2" color="gray" align="center">An app is connecting to BConnect.</Text>
      <Form.Root onSubmit={signIn} style={{ width: '100%' }}>
        <Flex direction="column" gap="5" style={{ alignItems: 'flex-start', alignSelf: 'stretch' }}>
          <Form.Field name="email" style={{ width: '100%' }}>
            <Form.Label style={{ display: 'flex', flexDirection: 'column', marginBottom: '4px' }}>
              <Text size="2" weight="medium">Email address</Text>
            </Form.Label>
            <Form.Control asChild>
              <TextField.Root type="email" value={email} onChange={(event) => setEmail(event.target.value)} />
            </Form.Control>
          </Form.Field>
          <Form.Field name="password" style={{ width: '100%' }}>
            <Form.Label style={{ display: 'flex', flexDirection: 'column', marginBottom: '4px' }}>
              <Text size="2" weight="medium">Password</Text>
            </Form.Label>
            <Form.Control asChild>
              <TextField.Root type="password" value={password} onChange={(event) => setPassword(event.target.value)} />
            </Form.Control>
          </Form.Field>
          {error ? (
            <Text size="1" color="red">{error}</Text>
          ) : (
            <Text size="1" style={{ visibility: 'hidden' }}>.</Text>
          )}
          <Button type="submit" size="2" variant="solid" disabled={submitting} style={{ alignSelf: 'stretch' }}>
            Sign in
          </Button>
        </Flex>
      </Form.Root>
    </ConnectShell>
  );
}
